import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Popover,
  Stack,
  Typography,
} from "@mui/material";

interface AuthorityFilterDropdownProps {
  label?: string;
  options: string[];
  value: string[];
  onChange: (value: string[]) => void;
}

const AuthorityFilterDropdown: React.FC<AuthorityFilterDropdownProps> = ({
  label = "Authorities",
  options,
  value,
  onChange,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const open = Boolean(anchorEl);
  const id = open ? "authority-filter-popover" : undefined;

  const allSelected = value.length === options.length;
  const selectedLabel = useMemo(() => {
    if (allSelected || value.length === 0) return "All";
    if (value.length === 1) return value[0];
    return `${value.length} selected`;
  }, [allSelected, value]);

  const toggleOption = (opt: string) => {
    if (value.includes(opt)) onChange(value.filter((v) => v !== opt));
    else onChange([...value, opt]);
  };

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleSelectAllToggle = () => {
    onChange(allSelected ? [] : [...options]);
  };

  return (
    <>
      <Button
        aria-describedby={id}
        variant="outlined"
        size="small"
        onClick={handleOpen}
        sx={{ textTransform: 'none' }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2" color="text.secondary">{label}:</Typography>
          <Typography variant="body2" fontWeight={600}>{selectedLabel}</Typography>
        </Stack>
      </Button>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            width: 340,
            minHeight: '40vh',
            maxHeight: '70vh',
            display: 'flex',
            flexDirection: 'column',
          }
        }}
      >
        <Stack spacing={1} sx={{ p: 1.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2">{label}</Typography>
            <Button size="small" onClick={handleSelectAllToggle} sx={{ textTransform: 'none' }}>
              {allSelected ? 'Clear all' : 'Select all'}
            </Button>
          </Stack>
          <Divider />
        </Stack>

        <Box sx={{ px: 1.5, pb: 1.5, overflowY: 'auto' }}>
          <Stack spacing={0.5}>
            {options.map((opt) => (
              <FormControlLabel
                key={opt}
                control={
                  <Checkbox
                    size="small"
                    checked={value.includes(opt)}
                    onChange={() => toggleOption(opt)}
                  />
                }
                label={<Typography variant="body2">{opt}</Typography>}
              />
            ))}
          </Stack>
        </Box>

        <Divider />
        <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={handleClose} size="small" variant="contained" sx={{ textTransform: 'none' }}>
            Apply
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default AuthorityFilterDropdown;


